import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { DataFilterService, FILTER_KEYS } from '../../../services/data-filter.service';
import { FilterKey, FilterOption, FilterSelection } from '../../../models/dataFilter/filterCatalog.model';
import { AuthService } from '../../../services/auth.service';
import { MatListModule } from '@angular/material/list';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

/** Case-insensitive match on the label or the secondary line, so processor
 *  domains, a state's country and a site's processor are all searchable. */
function matches(option: FilterOption, term: string): boolean {
  return option.label.toLowerCase().includes(term)
    || (option.detail?.toLowerCase().includes(term) ?? false);
}

const EMPTY_QUERY = Object.fromEntries(FILTER_KEYS.map(key => [key, ''])) as Record<FilterKey, string>;

/**
 * Cascading data-scope filters, shown in the dialog opened from the filter
 * bar. Options come from the user's pre-fetched filter catalog, and every rule
 * between dimensions lives in DataFilterService — this component only holds
 * the in-progress selection, which the Filter button commits.
 *
 * A dimension with a single reachable option is auto-selected and locked;
 * Business Type and Processor are hidden outright when the account only ever
 * has one.
 */
@Component({
  selector: 'app-data-filter',
  imports: [MatFormFieldModule, MatSelectModule, MatButtonModule, MatIconModule, MatDialogModule, MatToolbarModule, MatCardModule, MatListModule, NgClass, FormsModule, NgxMatSelectSearchModule],
  templateUrl: './data-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './data-filter.component.scss'
})
export class DataFilterComponent {
  readonly filters = inject(DataFilterService);
  readonly auth = inject(AuthService);
  private readonly dialogRef = inject(MatDialogRef<DataFilterComponent>, { optional: true });

  readonly keys = FILTER_KEYS;

  /** The in-progress selection — committed by Filter, discarded by Close. */
  readonly draft = signal<FilterSelection>(this.filters.cascade(this.filters.applied()).selection);

  /** Every dimension's reachable options, from one top-down pass. */
  readonly options = computed(() => this.filters.cascade(this.draft()).options);

  /** A dimension with a single reachable option is locked — auto-selected and disabled. */
  readonly locked = computed(() => Object.fromEntries(
    FILTER_KEYS.map(key => [key, this.options()[key].length <= 1])
  ) as Record<FilterKey, boolean>);

  /** Every dimension locked — nothing to choose, so the actions would do nothing. */
  readonly actionsDisabled = computed(() => FILTER_KEYS.every(key => this.locked()[key]));

  /**
   * Each closed field's text. Material builds it from every selected option's
   * full text content, which would now include the detail line, so it's
   * rendered from the labels alone — same order and separator as Material's.
   */
  readonly triggerText = computed(() => Object.fromEntries(
    FILTER_KEYS.map(key => {
      const selected = this.draft()[key];
      const labels = this.options()[key]
        .filter(option => selected.includes(option.value))
        .map(option => option.label);
      return [key, labels.join(', ')];
    })
  ) as Record<FilterKey, string>);

  /** What each dimension's search box holds. */
  readonly query = signal<Record<FilterKey, string>>(EMPTY_QUERY);

  /** Each dimension's options narrowed by its search box — what the panel lists. */
  readonly visibleOptions = computed(() => {
    const query = this.query();
    return Object.fromEntries(FILTER_KEYS.map(key => {
      const term = query[key].trim().toLowerCase();
      const options = this.options()[key];
      return [key, term ? options.filter(option => matches(option, term)) : options];
    })) as Record<FilterKey, FilterOption[]>;
  });

  onSearch(key: FilterKey, text: string): void {
    this.query.update(query => ({ ...query, [key]: text }));
  }

  /**
   * A change from the panel. mat-select only knows about the options it is
   * showing, so while a search is active its new value leaves out selections
   * the search has hidden. Merge those back instead of deselecting them.
   */
  selectFromPanel(key: FilterKey, values: string[]): void {
    const shown = new Set(this.visibleOptions()[key].map(option => option.value));
    const hidden = this.draft()[key].filter(value => !shown.has(value));
    this.select(key, [...new Set([...hidden, ...values])]);
  }

  /** One handler for every dropdown: update the dimension, then re-run the cascade. */
  select(key: FilterKey, values: string[]): void {
    this.draft.set(this.filters.cascade({ ...this.draft(), [key]: values }).selection);
  }

  apply(): void {
    this.filters.apply(this.draft());
    this.dialogRef?.close();
  }

  /** Reset the dropdowns to their defaults (does not apply or close). */
  clear(): void {
    this.draft.set(this.filters.defaultSelection());
    this.query.set(EMPTY_QUERY);
  }

  inactiveOption(option: FilterOption): string {

    if (this.auth.hasPermission('SHOW_BUSINESS_STATUS:Read')) {

      if (option.status === 'Inactive') {
        return 'inactive-option'
      } else {
        return ''
      }

    } else {
      return ''
    }

  }

}
